/**
 * QMS application roles — realized as **Entra App Roles** (claims-based,
 * read from `idTokenClaims.roles`), NOT SharePoint security groups
 * (04-CONTEXT.md §2). These supersede the AP-VERBATIM union
 * (`'AP' | 'CareManager' | 'Admin'`) carried over from harmony-ap at fork time.
 *
 * The role model is the matrix §3 (01-role-permissions-matrix.md), collapsed
 * onto the dashboard App Roles per the §5 identity map:
 *
 *  - `Oversight`     — Andy (Operations), Batra (Director/RN), QSO (Prathyusha
 *                      Kola). Full `All` read scope + oversight investigate.
 *                      Approval is HUMAN-only and is NOT a dashboard action
 *                      (authority floor §2) — see config/roles.ts.
 *  - `CareManager`   — SaH + NDIS Care Managers. `Portfolio` read scope.
 *  - `HRWHS`         — Amran Sean (HR / People & Culture / WHS).
 *                      `HR/WHS-touching` read scope.
 *  - `Finance`       — Amanda (PPT Finance Officer). `Finance-relevant` scope.
 *
 * Read-only field roles (Phase-5 magic-link surface, NOT the Phase-4 admin
 * office shell — included in the model so 04-03 can compute their `Own only`
 * scope, but DENIED by the admin RoleGuard):
 *  - `SupportWorker`, `NDISSubcontractor`, `AlliedProfessional`.
 */
export type AppRole =
  // Office roles (the Phase-4 admin shell audience — ~14 M365 staff)
  | 'Oversight'
  | 'CareManager'
  | 'HRWHS'
  | 'Finance'
  // Read-only field reporter roles (Phase-5 magic-link surface; modelled here
  // for scope resolution, denied entry to the admin shell)
  | 'SupportWorker'
  | 'NDISSubcontractor'
  | 'AlliedProfessional';

/**
 * The office roles allowed into the Phase-4 admin dashboard shell. Field
 * reporter roles are excluded (their surface is Phase-5). Used by the admin
 * RoleGuard in App.tsx.
 */
export const OFFICE_ROLES: readonly AppRole[] = [
  'Oversight',
  'CareManager',
  'HRWHS',
  'Finance',
] as const;

/** Authenticated user profile derived from MSAL account + ID token claims. */
export interface AuthUser {
  name: string;
  /** Primary login identity (account.username) — for display. */
  email: string;
  /** Entra App Roles from `idTokenClaims.roles`. */
  roles: AppRole[];
  /** Convenience flags for the four office roles. */
  isOversight: boolean;
  isCareManager: boolean;
  isHRWHS: boolean;
  isFinance: boolean;
  /** True when the login carries at least one office (admin-shell) role. */
  isOfficeUser: boolean;
  accountId: string;
}
